import { computed, toValue } from 'vue';
import { useQuery } from '@tanstack/vue-query';

// Cena z kodem dla kafelkow (GET /api/product/calculate_prices).
// Kazdy kafelek to osobna wyspa Vue z wlasnym useQuery, a loader skleja
// zapytania z jednej chwili w jedno wywolanie - strona listingu to jeden
// request zamiast kilkudziesieciu. Wspolny sharedQueryClient sprawia, ze
// ten sam produkt w ramce i na listingu nie jest liczony drugi raz.

const NO_CATEGORY = 'none';

// Cala strona listingu idzie jednym requestem (37 produktow na zimnym cache
// to ~0.8 s) - jeden worker PHP i jeden odczyt promocji na wejscie. Dzielimy
// dopiero powyzej limitu walidacji backendu (CalculatePricesFormRequest::MAX_PRODUCTS).
const CHUNK_SIZE = 60;

// Wyspy montuja sie w petli, a vue-query odpala queryFn z lekkim opoznieniem -
// krotkie okno zbiera je wszystkie do jednej kolejki.
const BATCH_WINDOW_MS = 16;

const queues = new Map();
let flushTimer = null;

export const isPriceWithCodeEnabled = () =>
    window.PRODUCT_ITEM_PROMOTION?.is_show_coupon_value === true;

// Na listingu kategorii kupon dobieramy pod jej booster; poza listingiem
// (karta, strona glowna) kategorii nie ma i backend bierze kupon domyslny.
export const currentCategoryId = () => window.CATEGORY_ID || null;

// Query skladane recznie: id i kategoria to same cyfry, wiec przecinki zostaja
// czytelne (bez %2C). Id posortowane - ten sam zestaw produktow daje zawsze ten
// sam URL, co pomaga cache przegladarki i CDN; odpowiedz i tak jest mapa po id.
const buildUrl = (categoryKey, ids) => {
    const sortedIds = [...ids]
        .sort((a, b) => Number(a) - Number(b))
        .map(encodeURIComponent)
        .join(',');
    const category =
        categoryKey !== NO_CATEGORY ? `&category_id=${encodeURIComponent(categoryKey)}` : '';

    return `/api/product/calculate_prices?ids=${sortedIds}${category}`;
};

const fetchChunk = async (categoryKey, ids) => {
    const response = await fetch(buildUrl(categoryKey, ids));

    if (!response.ok) {
        throw new Error(`calculate_prices: ${response.status}`);
    }

    return response.json();
};

const flush = () => {
    flushTimer = null;

    const pending = new Map(queues);
    queues.clear();

    pending.forEach((waiters, categoryKey) => {
        const ids = [...waiters.keys()];

        for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
            const chunk = ids.slice(i, i + CHUNK_SIZE);

            fetchChunk(categoryKey, chunk)
                .then((data) => {
                    chunk.forEach((id) =>
                        waiters.get(id).forEach(({ resolve }) => resolve(data?.[id] ?? null))
                    );
                })
                .catch((error) => {
                    chunk.forEach((id) => waiters.get(id).forEach(({ reject }) => reject(error)));
                });
        }
    });
};

const load = (categoryKey, productId) =>
    new Promise((resolve, reject) => {
        if (!queues.has(categoryKey)) {
            queues.set(categoryKey, new Map());
        }

        const waiters = queues.get(categoryKey);

        if (!waiters.has(productId)) {
            waiters.set(productId, []);
        }

        waiters.get(productId).push({ resolve, reject });

        if (!flushTimer) {
            flushTimer = setTimeout(flush, BATCH_WINDOW_MS);
        }
    });

export const usePriceWithCode = (productId, categoryId) => {
    const id = computed(() => {
        const value = toValue(productId);

        return value ? String(value) : null;
    });
    const categoryKey = computed(() => {
        const value = toValue(categoryId);

        return value ? String(value) : NO_CATEGORY;
    });

    return useQuery({
        queryKey: ['price-with-code', categoryKey, id],
        queryFn: ({ queryKey }) => load(queryKey[1], queryKey[2]),
        enabled: computed(() => isPriceWithCodeEnabled() && id.value !== null),
        // Backend trzyma wynik 5 min - po stronie klienta ta sama swiezosc.
        staleTime: 5 * 60 * 1000,
    });
};
